import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import React from "react";
import { useMutation, useQueryClient } from "react-query";
import { deleteRecord as deleteRecordApi } from "src/pages/api";

/**
 * Reusable DeleteRecord Component
 *
 * @param {Object}        props.row      - Record to delete (must have `id`)
 * @param {Function}      props.onClose  - Close the dialog
 * @param {string}        props.title    - Dialog title, e.g. "Delete Company"
 * @param {string}        props.endpoint - API base path, e.g. "/users"
 * @param {Function}      [props.deleteFn] - Override for special-case API calls
 * @param {string|Array}  props.queryKey - React Query key(s) to invalidate on success
 * @param {Function}      [props.onSuccess] - Callback after successful delete
 * @param {string}        [props.labelKey]  - Row field to show (auto-detects first non-id key)
 */
export default function DeleteRecord({
  row,
  onClose,
  title = "Delete Record",
  endpoint,
  deleteFn,
  queryKey,
  onSuccess,
  labelKey,
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const resolvedLabel = labelKey ? row?.[labelKey] : "";

  const callDelete = () => {
    if (deleteFn) return deleteFn({ id: row.id });
    return deleteRecordApi({ endpoint, id: row.id });
  };

  const mutation = useMutation(callDelete, {
    onSuccess: (data) => {
      if (data && data.success === false) {
        enqueueSnackbar(data.message || "Failed to delete", { variant: "error" });
        return;
      }
      enqueueSnackbar(data?.message || "Record deleted successfully", { variant: "success" });
      if (queryKey) {
        const keys = Array.isArray(queryKey) ? queryKey : [queryKey];
        keys.forEach((k) => queryClient.invalidateQueries(k));
      }
      if (onSuccess) onSuccess();
      else onClose();
    },
    onError: (error) => {
      const resData = error?.response?.data;
      const msg =
        resData?.message || resData?.error || error?.message || "Failed to delete";
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const handleClose = () => {
    if (!mutation.isLoading) onClose();
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: { xs: 0, sm: 2 }, m: { xs: 0, sm: 2 }, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={mutation.isLoading}
          sx={{ color: "grey.500" }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 } }}>
        <Typography variant="body1" textAlign="center">
          <Typography
            component="span"
            sx={{ fontWeight: 600, display: "block", mb: 1, fontSize: { xs: "1rem", sm: "1.1rem" } }}
          >
            {resolvedLabel}
          </Typography>
          Are you sure you want to delete this record?
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="center"
          spacing={{ xs: 1, sm: 2 }}
          sx={{ mt: 2.5 }}
        >
          <Button
            variant="contained"
            color="error"
            onClick={() => mutation.mutate()}
            disabled={mutation.isLoading}
            fullWidth={fullScreen}
          >
            {mutation.isLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            disabled={mutation.isLoading}
            fullWidth={fullScreen}
          >
            Cancel
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
